import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

const CHANNEL_HEADERS_LIVE = {
  'X-Channel': 'LINK',
  'X-Api-Mode': 'live',
} as const;

// Item (package) statuses — logistic Status Model v2 (docs/09-FE-CHANGES.md §1),
// in lifecycle order. Two names moved: `DELIVERED` → `COMPLETED` and
// `RETURNED` → `RETURNED_TO_HUB`. `HUB_SCANNED` / `DISPATCHED` /
// `HANDOVER_SCANNED` were deliberately kept (the rider app consumes them).
// `DAMAGED` is no longer a status at all — read `condition` instead (§4).
export const PACKAGE_STATUSES = [
  'CREATED',
  'HUB_SCANNED',
  'DISPATCHED',
  'HANDOVER_SCANNED',
  'IN_DELIVERY',
  'IN_RETURN',
  'COMPLETED',
  'RETURNED_TO_HUB',
  // No producer yet on the BE side — listed so the label/filter exists the day
  // items start reaching it.
  'RETURNED_TO_CLIENT',
  'LOST',
  'CANCELLED',
] as const;
export type PackageStatus = (typeof PACKAGE_STATUSES)[number];

/**
 * Physical condition of an item, orthogonal to `status` (Status Model v2 §4).
 * A damaged item no longer stops the flow — it keeps moving all the way to
 * `COMPLETED` — so in the UI this is a **badge, never a status column**: an item
 * can be `IN_DELIVERY` + `DAMAGED` at the same time.
 */
export type PackageCondition = 'OK' | 'DAMAGED';

/**
 * One proof-of-delivery photo for a package (POD-per-package), ingested by
 * logistic from the delivery-service `package-pod-uploaded` event. `mediaType`
 * groups photos by kind on the detail drawer (currently only `DELIVERY_PROOF`;
 * richer Dynamic-Form types may arrive later). Empty for non-POD clients.
 */
export interface PackageMedia {
  id: string;
  mediaType: string;
  url: string;
  latitude?: string;
  longitude?: string;
  capturedAt?: string | null;
}

/**
 * Rider who performed a transition (e.g. the handover scan), resolved by the
 * backend from the batch snapshot. Absent for system / ops-email transitions.
 */
export interface PackageTimelineRider {
  id: number;
  /** Rider code, e.g. `MTR000`. Null if the snapshot lacked one. */
  code?: string | null;
  /** Rider display name. Null if the snapshot lacked one. */
  name?: string | null;
}

/**
 * One item status transition, from logistic's `item_status_history` audit
 * log. Drives the package-detail lifecycle timeline. Oldest-first.
 */
export interface PackageTimelineEvent {
  id: string;
  fromStatus: string;
  toStatus: string;
  /** Actor email/identifier, or null (system-driven transition). */
  changedBy?: string | null;
  /** Rider identity when `changedBy` is a rider id (handover). Null otherwise. */
  rider?: PackageTimelineRider | null;
  /** Free-text note captured with the transition, or null. */
  note?: string | null;
  /** ISO timestamp of the transition. */
  changedAt: string;
}

export interface PackageRow {
  id: string;
  po: string;
  deliveryId: string;
  // Booking ID from the parent shipment (the "Booking ID" column on the
  // shipments page). Shown on the printed label. Optional until the
  // /v1/packages backend includes it.
  bookingId?: string;
  outlet: string;
  sender: string;
  // Owning client (company) — shown after the DASH wordmark on the printed label
  // header ("DASH | {client.name}"). Optional; the header omits it when absent.
  client?: { id: number; name: string };
  qty: number;
  // Jumlah koli (physical parcels) for this item. Distinct from `qty`
  // (`items.quantity`); BE default is 1. Optional until the /v1/packages
  // backend includes it.
  koli?: number;
  weight: number;
  // Volume in m³. Optional until the /v1/packages backend includes it; used to
  // prefill the edit-package modal.
  volume?: number;
  status: PackageStatus;
  // Physical condition (Status Model v2 §4) — shown as a badge next to the
  // status, never instead of it. null/absent = not inspected.
  condition?: PackageCondition | null;
  recipientAddress?: string;
  senderAddress?: string;
  itemName?: string;
  // Position of this item within its shipment (waybill) and the shipment's
  // total item count — drives the "X / Y" badge on the printed label. Optional
  // until the /v1/packages backend includes them.
  itemSeq?: number;
  itemTotal?: number;
  // Item creation timestamp (ISO) from the items table. Shown as "Created Date".
  createdAt?: string;
  // POD photos uploaded for this package (per jenis foto). Empty / absent when
  // none or the client isn't on POD-per-package.
  media?: PackageMedia[];
  /** Status-transition history, oldest-first. Empty/absent when none recorded. */
  timeline?: PackageTimelineEvent[];
}

export interface PackageListParams {
  search?: string;
  status?: PackageStatus;
  recipient?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export type LabelLayout = 'THERMAL_4X6' | 'THERMAL_100X100' | 'A4_2UP';

export interface PackageListResult {
  packages: PackageRow[];
  total: number;
  page: number;
  limit: number;
}

export const getPackages = async (
  params: PackageListParams
): Promise<PackageListResult> => {
  const res = await Axios.get('/v1/packages', {
    params,
    headers: CHANNEL_HEADERS_LIVE,
  });
  return res.data.data as PackageListResult;
};

/**
 * Per-status counts backing the dashboard cards. Keys follow Status Model v2
 * (docs/09-FE-CHANGES.md §2) — `total` is the sum of every other field.
 *
 * ⚠️ Name trap: `verifiedByHub` counts **`HUB_SCANNED`** and `verifiedByRider`
 * counts **`HANDOVER_SCANNED`**. The names are leftovers from a rename that was
 * abandoned halfway — there is no `VERIFIED_BY_*` status.
 *
 * Fields are optional so a partial/failed payload still renders (cards fall
 * back to an em-dash / 0).
 */
export interface PackagesSummary {
  total?: number;
  created?: number;
  verifiedByHub?: number; // = HUB_SCANNED
  dispatched?: number;
  verifiedByRider?: number; // = HANDOVER_SCANNED
  inDelivery?: number;
  completed?: number;
  inReturn?: number;
  returnedToHub?: number;
  returnedToClient?: number;
  cancelled?: number;
  lost?: number;
}

export const getPackagesSummary = async (): Promise<PackagesSummary> => {
  const res = await Axios.get('/v1/packages/summary', {
    headers: CHANNEL_HEADERS_LIVE,
  });
  return res.data.data as PackagesSummary;
};

/**
 * Soft-cancel a single package (item). The backend flips its status to
 * CANCELLED and recomputes the parent shipment's totals. Only CREATED /
 * HUB_SCANNED items are cancellable; the server rejects others.
 */
export const cancelPackage = async (
  id: string,
  reason?: string
): Promise<void> => {
  try {
    await Axios.post(
      `/v1/packages/${id}/cancel`,
      { reason: reason || undefined },
      { headers: CHANNEL_HEADERS_LIVE }
    );
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};

/**
 * Edit a package's weight (kg, > 0), volume (m³, ≥ 0) and koli (integer,
 * 1..10000). `koli` is REQUIRED by the backend — a body without it is rejected
 * with a 400 rather than silently resetting the stored value (koli-trd-v1
 * §8.11), so always send the current value even when only weight changes.
 */
export const updatePackage = async (
  id: string,
  body: { weight: number; volume: number; koli: number }
): Promise<void> => {
  try {
    await Axios.patch(`/v1/packages/${id}`, body, {
      headers: CHANNEL_HEADERS_LIVE,
    });
  } catch (error) {
    throw new Error(getBackendErrorMessage(error));
  }
};
